import { backendApi } from "@/services/backend/api";
import {
  AdditionalImageReq,
  AdditionalLibraryImageReq,
  CapsReq,
  ClearBaseReq,
  CreateReq,
  DeleteAdditionalImageReq,
  DeleteAdditionalLibraryImageReq,
  DetailsReq,
  ImageReq,
  ListReq,
  RenameReq,
  ReplaceAdditionalLibraryReq,
  ReplaceAdditionalsReq,
  ReplaceConsumablesReq,
  ReplacePestsReq,
  SetRentingReq,
  ShowImageRequest,
  SupplierReq,
  TimesReq,
  UpdateBasePriceReq,
  UpdateConsumableOverrideReq,
  VisitsReq,
} from "@/services/backend/qpm/monitors/types/monitor-commands";
import type { ConsumablesEnrichedRes } from "@/services/backend/qpm/monitors/types/monitor-view";
import type {
  AssetRefDTO,
  MonitorAdditionalLibraryItemDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { MonitorView } from "@/services/backend/qpm/monitors/types/monitor-view";
import {
  MonitorListView,
  MonitorServiceDefaults,
  MonitorServiceDefaultsList,
} from "@/services/backend/qpm/monitors/types/monitor";

const qpmMonitorServiceApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["QpmMonitor", "QpmMonitorType"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmMonitorCreate: build.mutation<{ id: string }, CreateReq>({
        query: (body) => ({
          url: "/qpm/monitors",
          method: "POST",
          body,
        }),
        invalidatesTags: ["QpmMonitor"],
      }),
      qpmMonitorGet: build.query<MonitorView, { id: string }>({
        query: (params) => ({
          url: "/qpm/monitors/get",
          method: "GET",
          params,
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmMonitor", id: result.id }] : [],
      }),
      // Base monitors list endpoint (not catalog-specific).
      // Backend expects base64 JSON in query param `request` (same convention as catalog list endpoints).
      qpmMonitorList: build.query<MonitorListView, Partial<ListReq>>({
        query: (request) => ({
          url: "/qpm/monitors",
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),
      qpmMonitorAdditionalsLibrary: build.query<
        { items: MonitorAdditionalLibraryItemDTO[] },
        void
      >({
        query: () => ({
          url: "/qpm/monitors/additionals/library",
          method: "GET",
        }),
        providesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),
      qpmMonitorReplaceAdditionalLibrary: build.mutation<
        null,
        ReplaceAdditionalLibraryReq
      >({
        query: (body) => ({
          url: "/qpm/monitors/additionals/library",
          method: "PUT",
          body,
        }),
        invalidatesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),
      // Keep the existing catalog list as-is.
      qpmCatalogMonitorList: build.query<MonitorListView, Partial<ListReq>>({
        query: (request) => ({
          url: "/qpm/catalog/monitors",
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),
      qpmMonitorRename: build.mutation<null, RenameReq>({
        query: (body) => ({
          url: "/qpm/monitors/name",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorSupplier: build.mutation<null, SupplierReq>({
        query: (body) => ({
          url: "/qpm/monitors/supplier",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorCaps: build.mutation<null, CapsReq>({
        query: (body) => ({
          url: "/qpm/monitors/caps",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorBasePrice: build.mutation<null, UpdateBasePriceReq>({
        query: (body) => ({
          url: "/qpm/monitors/baseprice",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorClearBasePrice: build.mutation<null, ClearBaseReq>({
        query: (body) => ({
          url: "/qpm/monitors/baseprice/clear",
          method: "POST",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorTimes: build.mutation<null, TimesReq>({
        query: (body) => ({
          url: "/qpm/monitors/times",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorVisits: build.mutation<null, VisitsReq>({
        query: (body) => ({
          url: "/qpm/monitors/visits",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorRenting: build.mutation<null, SetRentingReq>({
        query: (body) => ({
          url: "/qpm/monitors/renting",
          method: "POST",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.monitorId }],
      }),

      qpmMonitorImage: build.mutation<null, ImageReq>({
        query: (request) => {
          const body = new FormData();
          body.append("monitorId", request.monitorId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: `/qpm/monitors/image`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmMonitor", id: request.monitorId },
        ],
      }),
      qpmMonitorDeleteImage: build.mutation<null, { monitorId: string }>({
        query: (body) => ({
          url: `/qpm/monitors/image`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmMonitor", id: request.monitorId },
        ],
      }),

      qpmMonitorShowImage: build.query<{ objectURL: string }, ShowImageRequest>(
        {
          query: (request) => ({
            url: `/qpm/monitors/image`,
            method: "GET",
            params: request,
            responseHandler: async (response) => {
              if (response.status !== 200) {
                return response.json();
              }
              return {
                objectURL: window.URL.createObjectURL(await response.blob()),
              };
            },
            // IMPORTANT. Since this query will download the Image under no circumstances
            // should the results be cached. This could end up using the users memory.
            cache: "no-cache",
          }),
        },
      ),

      qpmMonitorDetails: build.mutation<null, DetailsReq>({
        query: (body) => ({
          url: "/qpm/monitors/details",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorReplaceAdditionals: build.mutation<null, ReplaceAdditionalsReq>(
        {
          query: (body) => ({
            url: "/qpm/monitors/additionals",
            method: "PUT",
            body,
          }),
          invalidatesTags: (r, e, b) => [
            { type: "QpmMonitor", id: b.id },
            { type: "QpmMonitor", id: "LIST" },
          ],
        },
      ),
      qpmMonitorAdditionalImage: build.mutation<
        AssetRefDTO,
        AdditionalImageReq
      >({
        query: (request) => {
          const body = new FormData();
          body.append("monitorId", request.monitorId);
          body.append("additionalId", request.additionalId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: `/qpm/monitors/additionals/image`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmMonitor", id: request.monitorId },
          { type: "QpmMonitor", id: "LIST" },
        ],
      }),
      qpmMonitorDeleteAdditionalImage: build.mutation<
        null,
        DeleteAdditionalImageReq
      >({
        query: (body) => ({
          url: `/qpm/monitors/additionals/image`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmMonitor", id: request.monitorId },
          { type: "QpmMonitor", id: "LIST" },
        ],
      }),
      qpmMonitorAdditionalLibraryImage: build.mutation<
        AssetRefDTO,
        AdditionalLibraryImageReq
      >({
        query: (request) => {
          const body = new FormData();
          body.append("additionalId", request.additionalId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: `/qpm/monitors/additionals/library/image`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),
      qpmMonitorDeleteAdditionalLibraryImage: build.mutation<
        null,
        DeleteAdditionalLibraryImageReq
      >({
        query: (body) => ({
          url: `/qpm/monitors/additionals/library/image`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: [{ type: "QpmMonitor", id: "LIST" }],
      }),

      qpmMonitorReplacePests: build.mutation<null, ReplacePestsReq>({
        query: (body) => ({
          url: "/qpm/monitors/pests",
          method: "PUT",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
      }),

      qpmMonitorReplaceConsumables: build.mutation<null, ReplaceConsumablesReq>(
        {
          query: (body) => ({
            url: "/qpm/monitors/consumables",
            method: "PUT",
            body,
          }),
          invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.id }],
        },
      ),

      qpmMonitorConsumablesOverride: build.mutation<
        null,
        UpdateConsumableOverrideReq
      >({
        query: (body) => ({
          url: "/qpm/monitors/consumables/override",
          method: "PATCH",
          body,
        }),
        invalidatesTags: (r, e, b) => [{ type: "QpmMonitor", id: b.monitorId }],
      }),

      qpmMonitorServiceDefaultsPatch: build.mutation<
        MonitorServiceDefaults,
        MonitorServiceDefaults
      >({
        query: (request) => ({
          url: `/qpm/monitor/servicedefaults`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result) =>
          result ? [{ type: "QpmMonitorType" }] : [],
      }),
      qpmMonitorServiceDefaultsList: build.query<
        MonitorServiceDefaultsList,
        Record<string, string>
      >({
        query: (request) => ({
          url: `/qpm/monitor/servicedefaults/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmMonitorType", id: "LIST" }] : [],
      }),
      qpmMonitorDelete: build.mutation<null, { id: string }>({
        query: (body) => ({
          url: `/qpm/monitors`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: (r, e, b) => [
          { type: "QpmMonitor", id: b.id },
          { type: "QpmMonitor", id: "LIST" },
        ],
      }),

      qpmMonitorConsumablesEnriched: build.query<
        ConsumablesEnrichedRes,
        { id: string }
      >({
        query: (params) => ({
          url: "/qpm/monitors/consumables/enriched",
          method: "GET",
          // Backend expects query param `id`
          params,
        }),
        providesTags: (_result, _error, arg) => [
          { type: "QpmMonitor", id: arg.id },
        ],
      }),
    }),
  });

export const {
  useQpmMonitorCreateMutation,
  useQpmMonitorGetQuery,
  useQpmCatalogMonitorListQuery,
  useQpmMonitorRenameMutation,
  useQpmMonitorSupplierMutation,
  useQpmMonitorCapsMutation,
  useQpmMonitorBasePriceMutation,
  useQpmMonitorClearBasePriceMutation,
  useQpmMonitorTimesMutation,
  useQpmMonitorVisitsMutation,
  useQpmMonitorRentingMutation,
  useQpmMonitorImageMutation,
  useQpmMonitorDeleteImageMutation,
  useQpmMonitorShowImageQuery,
  useQpmMonitorDetailsMutation,
  useQpmMonitorReplaceAdditionalLibraryMutation,
  useQpmMonitorReplaceAdditionalsMutation,
  useQpmMonitorAdditionalImageMutation,
  useQpmMonitorDeleteAdditionalImageMutation,
  useQpmMonitorAdditionalLibraryImageMutation,
  useQpmMonitorDeleteAdditionalLibraryImageMutation,
  useQpmMonitorReplacePestsMutation,
  useQpmMonitorReplaceConsumablesMutation,
  useQpmMonitorConsumablesOverrideMutation,
  useQpmMonitorServiceDefaultsPatchMutation,
  useQpmMonitorServiceDefaultsListQuery,
  useQpmMonitorDeleteMutation,
  useQpmMonitorConsumablesEnrichedQuery,
  useQpmMonitorListQuery,
  useQpmMonitorAdditionalsLibraryQuery,
} = qpmMonitorServiceApi;
